import { createPortal } from "react-dom";
import { Moon, LogIn, Clock } from "lucide-react";
import { useAttendance } from "@/hooks/useAttendance";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/lang";

const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

/**
 * What the floor sees without pressing anything (owner, 2026-11-03):
 * - the split-break prompt when the gap is due: PAUSE DUTY or STILL WORKING (15 min).
 *   Running tables hold it, and are named. If nobody taps, the server pauses duty
 *   once the tables are settled.
 * - in the gap, a START DUTY bar: big from 15 minutes before the second part.
 * Everything comes from the server's status (useAttendance); nothing is stored.
 * Mounted once per screen, inside AttendanceProvider.
 */
export function AttendancePrompts({ token }) {
  const { status, acting, pauseGap, postponeGap, resume } = useAttendance(token);
  if (!status || status.exempt) return null;
  const state = status.state;
  const gap = status.gap;
  const working = state === "ON_DUTY" || state === "ON_BREAK";
  const showGapPrompt = working && gap && !gap.postponed_until;
  const held = gap?.held_by || [];

  if (showGapPrompt) {
    return createPortal(
      <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
        role="alertdialog" aria-modal="true" data-testid="attendance-gap-prompt">
        <div className="w-full sm:max-w-sm bg-card border-t sm:border rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 space-y-4 text-card-foreground">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-info/10 text-info flex items-center justify-center shrink-0">
              <Moon className="w-6 h-6" />
            </span>
            <div>
              <h3 className="text-base font-bold">{t("स्प्लिट ब्रेक का समय · Split break time")}</h3>
              <p className="text-sm text-muted-foreground">
                {t(`${clock(gap.starts_at)} – ${clock(gap.ends_at)}. ड्यूटी रुकेगी, साइन-इन रहेगा · Duty pauses; you stay signed in.`)}
              </p>
            </div>
          </div>
          {held.length > 0 && (
            <p className="text-sm font-medium text-warning bg-warning/10 rounded-md px-3 py-2" data-testid="attendance-gap-held">
              {t(`पहले ये टेबल बंद करें · Waits for: ${held.join(", ")}`)}
            </p>
          )}
          <div className="space-y-2">
            <Button variant="action" className="w-full h-12 text-base font-bold" disabled={acting || held.length > 0}
              onClick={() => pauseGap()} data-testid="attendance-gap-pause">
              {t("ड्यूटी रोकें · PAUSE DUTY")}
            </Button>
            <Button variant="outline" className="w-full h-12 text-base font-semibold" disabled={acting}
              onClick={() => postponeGap()} data-testid="attendance-gap-postpone">
              <Clock className="w-4 h-4 mr-2" />
              {t("अभी काम चल रहा है · STILL WORKING (15 min)")}
            </Button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  if (state === "ON_SPLIT_GAP" && status.can_resume) {
    const prominent = Boolean(status.resume?.prominent);
    return createPortal(
      <div className={`fixed inset-x-0 bottom-0 z-[105] border-t bg-card shadow-2xl ${prominent ? "p-4" : "p-2"}`}
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }} data-testid="attendance-resume-bar">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          {!prominent && (
            <span className="text-sm text-muted-foreground flex-1">
              {t(`स्प्लिट ब्रेक, ${clock(status.resume?.due_at)} पर वापस · Split break, back at ${clock(status.resume?.due_at)}`)}
            </span>
          )}
          <Button variant="action" disabled={acting} onClick={() => resume()} data-testid="attendance-resume-bar-btn"
            className={prominent ? "w-full h-14 text-lg font-bold" : "h-11 px-4 font-bold"}>
            <LogIn className={prominent ? "w-6 h-6 mr-2" : "w-4 h-4 mr-2"} />
            {t("ड्यूटी शुरू करें · START DUTY")}
          </Button>
        </div>
      </div>,
      document.body
    );
  }
  return null;
}
